import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: number;
  type: ToastType;
  title?: string;
  message: string;
  timeOut: number;
  progressBar: boolean;
}

export interface ToastOptions {
  timeOut?: number;
  progressBar?: boolean;
  // Accepted for API compatibility with ngx-toastr callers; positioning is
  // fixed to bottom/full-width in the toast container, so this is ignored.
  positionClass?: string;
}

// Minimal drop-in replacement for ngx-toastr's ToastrService (only the
// .error() call with message/title/options is actually used in this app).
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private nextId = 0;
  readonly toasts = signal<ToastMessage[]>([]);

  success(message: string, title?: string, options?: ToastOptions): void {
    this.show('success', message, title, options);
  }

  error(message: string, title?: string, options?: ToastOptions): void {
    this.show('error', message, title, options);
  }

  warning(message: string, title?: string, options?: ToastOptions): void {
    this.show('warning', message, title, options);
  }

  info(message: string, title?: string, options?: ToastOptions): void {
    this.show('info', message, title, options);
  }

  dismiss(id: number): void {
    this.toasts.update(list => list.filter(t => t.id !== id));
  }

  private show(type: ToastType, message: string, title?: string, options?: ToastOptions): void {
    const id = ++this.nextId;
    const timeOut = options?.timeOut ?? 5000;
    const progressBar = options?.progressBar ?? false;

    this.toasts.update(list => [...list, { id, type, title, message, timeOut, progressBar }]);

    if (timeOut > 0) {
      setTimeout(() => this.dismiss(id), timeOut);
    }
  }
}
